import { useState } from "react";
import type { FormEvent } from "react";
import FormModal from "@/components/FormModal/FormModal";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import { downloadBlob } from "@/lib/downloadExcel";
import toast from "@/lib/toast";
import type { ExportFileResult } from "@/types/common";
import styles from "./ExportConfirmationModal.module.scss";
import { combineClasses } from "@/lib/utils";

export interface ExportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchExportFile: () => Promise<ExportFileResult>;
  fallbackFileName: string;
  title?: string;
  message?: string;
  className?: string;
}

const ExportConfirmationModal = ({
  isOpen,
  onClose,
  fetchExportFile,
  fallbackFileName,
  title = "Export",
  message = "Are you sure you want to export the records?",
  className,
}: ExportConfirmationModalProps) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { blob, fileName } = await fetchExportFile();
      downloadBlob(blob, fileName || `${fallbackFileName}.xlsx`);
      onClose();
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleExport();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={onSubmit}
      submitLabel={isExporting ? "Exporting…" : "Export"}
      submitIcon={<DownloadIcon />}
      submitDisabled={isExporting}
      className={combineClasses(styles.exportModal, className)}
    >
      <p className={styles.message}>{message}</p>
    </FormModal>
  );
};

export default ExportConfirmationModal;
